/* eslint-disable max-lines */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { flushSync } from 'react-dom';
import dayjs from 'dayjs';
import Select from 'react-select';
import CustomDatePicker from '../../customDatePicker';
import UseAxios from '../../../../../Api/http/http';
import { useSnackbar } from '../../../../../Components/Snackbar/snackBarProvider';
import {
  getScrums,
  getStaffingRequests,
  createTbhResourceAllocation,
  updateTbhResourceAllocation,
  getResourceTypes,
  getRoleExperienceMappings,
  getTbhEmployees,
  getEmployeeExperiences,
  getLocationTypes,
  getDepartmentsList,
} from '../../../../../Api/request/projectDetails';
import {
  getRegionsData,
  getVerticalData,
  regionCountryData,
} from '../../../../../Api/request/projectOverviewMasterData';
import { getTbhDetailsByTbhId } from '../../../../../Api/request/tbhEmployeeManagement';

import './addTbhResourceModal.scss';


/* ─────────────────────────────────────────────────────────
   Field atom helper for react-select
   ───────────────────────────────────────────────────────── */
const childrenToOptions = (children) => {
  const nodes = React.Children.toArray(children);
  return nodes
    .filter((node) => node?.type === 'option')
    .map((node) => ({
      value: String(node.props.value ?? ''),
      label: String(node.props.children ?? ''),
      isDisabled: node.props.disabled || false,
    }));
};

const getExperienceLabel = (experience) => (
  experience?.experienceRange
  || experience?.experience_range
  || experience?.experience
  || experience?.label
  || ''
);

const normalizeExperienceLabel = (value) => String(value ?? '')
  .trim()
  .replace(/\s*(yrs?|years?)$/i, '')
  .replace(/\s*-\s*/g, '-')
  .replace(/\s*\+\s*$/g, '+')
  .replace(/\s+/g, ' ');

const getExperienceSortParts = (value) => {
  const label = normalizeExperienceLabel(value).toLowerCase();
  const compactLabel = label.replace(/[\s_-]/g, '');

  if (compactLabel === 'junior') return { group: 1, start: 0, end: 0 };
  if (compactLabel === 'midlevel') return { group: 1, start: 1, end: 1 };
  if (compactLabel === 'senior') return { group: 1, start: 2, end: 2 };

  const rangeMatch = label.match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)/);
  if (rangeMatch) {
    return { group: 0, start: Number(rangeMatch[1]), end: Number(rangeMatch[2]) };
  }

  const plusMatch = label.match(/^(\d+(?:\.\d+)?)\+$/);
  if (plusMatch) {
    return { group: 0, start: Number(plusMatch[1]), end: Number(plusMatch[1]) };
  }

  const numericMatch = label.match(/^(\d+(?:\.\d+)?)$/);
  if (numericMatch) {
    return { group: 2, start: Number(numericMatch[1]), end: Number(numericMatch[1]) };
  }

  return { group: 3, start: 0, end: 0 };
};

const sortExperienceOptions = (options = [], getLabel = getExperienceLabel) => (
  [...options].sort((first, second) => {
    const firstLabel = normalizeExperienceLabel(getLabel(first));
    const secondLabel = normalizeExperienceLabel(getLabel(second));
    const firstSort = getExperienceSortParts(firstLabel);
    const secondSort = getExperienceSortParts(secondLabel);

    return firstSort.group - secondSort.group
      || firstSort.start - secondSort.start
      || firstSort.end - secondSort.end
      || firstLabel.localeCompare(secondLabel, undefined, { numeric: true, sensitivity: 'base' });
  })
);

const updateRoleAndResetExperience = (setForm, roleId) => {
  flushSync(() => {
    setForm((prev) => ({
      ...prev,
      role_id: roleId ? String(roleId) : '',
      experience_id: '',
    }));
  });
};

const FieldSelect = ({
  labelText,
  isRequired,
  value,
  onChange,
  isDisabled,
  children,
}) => {
  const options = childrenToOptions(children);
  const placeholderOption = options.find((opt) => opt.value === '');
  const realOptions = options.filter((opt) => opt.value !== '');
  const selectedOption = realOptions.find((opt) => opt.value === value) || null;

  const handleChange = useCallback(
    (selected) => {
      onChange({ target: { value: selected ? selected.value : '' } });
    },
    [onChange],
  );

  return (
    <div>
      <label className="add-tbh-modal-field-label">
        {labelText}
        {isRequired && <span className="add-tbh-modal-required"> *</span>}
      </label>
      <Select
        options={realOptions}
        value={selectedOption}
        onChange={handleChange}
        isDisabled={isDisabled}
        isSearchable
        isClearable={false}
        placeholder={placeholderOption ? placeholderOption.label : 'Select'}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        classNamePrefix="arm-sel"
        classNames={{
          control: () => 'arm-sel-control',
          valueContainer: () => 'arm-sel-value-container',
          singleValue: () => 'arm-sel-single-value',
          placeholder: () => 'arm-sel-placeholder',
          input: () => 'arm-sel-input-container',
          indicatorsContainer: () => 'arm-sel-indicators-container',
          indicatorSeparator: () => 'arm-sel-indicator-separator',
          dropdownIndicator: () => 'arm-sel-dropdown-indicator',
          clearIndicator: () => 'arm-sel-clear-indicator',
          menu: () => 'arm-sel-menu',
          menuPortal: () => 'arm-sel-menu-portal',
          option: () => 'arm-sel-option',
        }}
      />
      {isRequired && (
        <input
          tabIndex={-1}
          style={{ opacity: 0, height: 0, position: 'absolute' }}
          required
          value={value}
          onChange={() => {}}
        />
      )}
    </div>
  );
};

FieldSelect.propTypes = {
  labelText: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};
FieldSelect.defaultProps = { isRequired: false, isDisabled: false };


/* ─────────────────────────────────────────────────────────
   AddTbhResourceModal
   ───────────────────────────────────────────────────────── */
// eslint-disable-next-line max-lines-per-function
const AddTbhResourceModal = ({
  onClose,
  projectId,
  projectStatus,
  onRefreshData,
  allocationData,
}) => {
  const { snackbarOpen } = useSnackbar();

  // Dropdown lists
  const [staffingRequests, setStaffingRequests] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [roles, setRoles] = useState([]);
  const [scrums, setScrums] = useState([]);
  const [tbhEmployees, setTbhEmployees] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [roleExperienceMappings, setRoleExperienceMappings] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [departments, setDepartments] = useState([]);

  // States
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [blockingMessage, setBlockingMessage] = useState(null);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [tbhAutoPopulated, setTbhAutoPopulated] = useState(false);
  const tbhManualSelectionRef = useRef({
    roleId: false,
    experienceId: false,
  });

  const resolveExperienceId = useCallback((candidate) => {
    if (candidate === null || candidate === undefined || candidate === '') return '';
    const candidateStr = String(candidate);
    const byId = experiences.find((opt) => String(opt.id ?? opt.value ?? '') === candidateStr);
    if (byId) return String(byId.id ?? byId.value ?? '');

    const normalize = (value) => String(value ?? '').trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    const byLabel = experiences.find((opt) => normalize(
      opt.experienceRange ?? opt.experience_range ?? opt.experience ?? opt.label ?? '',
    ) === normalize(candidateStr));

    return String(byLabel?.id ?? byLabel?.value ?? '');
  }, [experiences]);

  // Form Data
  const [formData, setFormData] = useState({
    tbh_id: '',
    staffing_req_id: '',
    region_id: '',
    country_id: '',
    vertical_id: '',
    role_id: '',
    experience_id: '',
    location_id: '',
    department_id: '',
    allocation_pct: '100',
    allocation_start_date: '',
    allocation_end_date: '',
    scrum_team_id: '',
    remark: '',
  });

  const experienceOptionsByRole = useMemo(() => {
    const optionsByRole = new Map();

    roleExperienceMappings.forEach((mapping, index) => {
      const roleId = String(mapping.roleId ?? mapping.role_id ?? '');
      const experienceId = String(
        mapping.experienceId
          ?? mapping.experience_id
          ?? mapping.experienceCategoryId
          ?? mapping.experience_category_id
          ?? '',
      );

      if (!roleId || !experienceId) return;

      if (!optionsByRole.has(roleId)) {
        optionsByRole.set(roleId, new Map());
      }

      optionsByRole.get(roleId).set(experienceId, index);
    });

    return optionsByRole;
  }, [roleExperienceMappings]);

  const filteredExperiences = useMemo(() => {
    const roleId = String(formData.role_id || '');
    if (!roleId) return [];

    const allowedExperiences = experienceOptionsByRole.get(roleId);
    if (!allowedExperiences) return [];

    return sortExperienceOptions(
      experiences.filter((opt) => allowedExperiences.has(String(opt.id ?? opt.value ?? ''))),
      getExperienceLabel,
    );
  }, [experiences, experienceOptionsByRole, formData.role_id]);

  // Pre-fill form if editing
  useEffect(() => {
    if (allocationData) {
      const raw = allocationData.rawRow || allocationData;
      const directExperienceId = raw.experienceId
        ?? raw.experience_id
        ?? allocationData.experienceId
        ?? allocationData.experience_id;
      const experienceId = directExperienceId !== null && directExperienceId !== undefined && directExperienceId !== ''
        ? String(directExperienceId)
        : resolveExperienceId(
          raw.experience
          ?? raw.experienceCategory
          ?? allocationData.experience
          ?? allocationData.experienceCategory
          ?? '',
        );
      setFormData({
        tbh_id: String(raw.tbhId ?? raw.tbh_id ?? ''),
        staffing_req_id: String(raw.requestId ?? raw.request_id ?? raw.staffing_req_id ?? ''),
        region_id: String(raw.regionId ?? raw.region_id ?? ''),
        country_id: String(raw.countryId ?? raw.country_id ?? ''),
        vertical_id: String(raw.verticalId ?? raw.vertical_id ?? ''),
        role_id: String(raw.roleId ?? raw.role_id ?? ''),
        experience_id: String(experienceId),
        location_id: String(raw.locationId ?? raw.location_id ?? ''),
        department_id: String(raw.departmentId ?? raw.department_id ?? ''),
        allocation_pct: String(raw.allocationPct ?? raw.allocation_pct ?? '100'),
        allocation_start_date: raw.allocationStartDate
          ? dayjs(raw.allocationStartDate).format('YYYY-MM-DD')
          : (raw.allocation_start_date
            ? dayjs(raw.allocation_start_date).format('YYYY-MM-DD')
            : (raw.startDate ? dayjs(raw.startDate).format('YYYY-MM-DD') : '')),
        allocation_end_date: raw.allocationEndDate
          ? dayjs(raw.allocationEndDate).format('YYYY-MM-DD')
          : (raw.allocation_end_date
            ? dayjs(raw.allocation_end_date).format('YYYY-MM-DD')
            : (raw.endDate ? dayjs(raw.endDate).format('YYYY-MM-DD') : '')),
        scrum_team_id: String(raw.scrumTeamId ?? raw.scrum_team_id ?? ''),
        remark: raw.remark ?? raw.remarks ?? '',
      });
    }
  }, [allocationData, experiences, resolveExperienceId]);

  // Load dropdown options
  useEffect(() => {
    if (!projectId) return;

    const fetchMetadata = async () => {
      setLoadingMetadata(true);
      setBlockingMessage(null);
      setFormError(null);
      try {
        if (projectStatus && projectStatus !== 'In Progress') {
          setBlockingMessage(
            `TBH Resources cannot be allocated to a "${projectStatus}" project.`
            + ' Change the project status to "In Progress" first.',
          );
          return;
        }

        const [
          scrumsRes,
          staffingRes,
          regionsRes,
          verticalsRes,
          rolesRes,
          roleExperienceMappingsRes,
          tbhEmployeesRes,
          experiencesRes,
          locationsRes,
          departmentsRes,
        ] = await Promise.all([
          UseAxios(getScrums()).catch(() => ({ data: { data: [] } })),
          UseAxios(getStaffingRequests(projectId)).catch(() => ({ data: { data: [] } })),
          UseAxios(getRegionsData()).catch(() => ({ data: [] })),
          UseAxios(getVerticalData()).catch(() => ({ data: [] })),
          UseAxios(getResourceTypes()).catch(() => ({ data: [] })),
          UseAxios(getRoleExperienceMappings()).catch(() => ({
            data: { roleExperienceMappings: [] },
          })),
          UseAxios(getTbhEmployees()).catch(() => ({ data: { tbhEmployeeList: [] } })),
          UseAxios(getEmployeeExperiences()).catch(() => ({ data: { employeeExperienceList: [] } })),
          UseAxios(getLocationTypes()).catch(() => ({ data: { locationTypeList: [] } })),
          UseAxios(getDepartmentsList()).catch(() => ({ data: { departments: [] } })),
        ]);

        const scrumsData = scrumsRes?.data;
        const scrumsList = Array.isArray(scrumsData)
          ? scrumsData
          : (scrumsData?.data || scrumsData?.result || []);
        setScrums(Array.isArray(scrumsList) ? scrumsList : []);

        const tbhEmployeesData = tbhEmployeesRes?.data;
        const tbhEmployeesList = Array.isArray(tbhEmployeesData)
          ? tbhEmployeesData
          : (tbhEmployeesData?.tbhEmployeeList || tbhEmployeesData?.data || []);
        setTbhEmployees(Array.isArray(tbhEmployeesList) ? tbhEmployeesList : []);

        const staffingData = staffingRes?.data;
        const staffingList = Array.isArray(staffingData)
          ? staffingData
          : (staffingData?.data || staffingData?.result || []);
        const finalStaffingList = Array.isArray(staffingList) ? [...staffingList] : [];
        if (allocationData) {
          const raw = allocationData.rawRow || allocationData;
          const currentReqId = raw.request_id ?? raw.staffing_req_id ?? raw.requestId;
          if (currentReqId && currentReqId !== 0 && currentReqId !== '0') {
            const exists = finalStaffingList.some(sr => {
              const val = sr.request_id ?? sr.id ?? sr.value;
              return String(val) === String(currentReqId);
            });
            if (!exists) {
              const currentSrNumber = raw.sr_number ?? raw.srNumber ?? `SRN-${currentReqId}`;
              finalStaffingList.push({
                request_id: currentReqId,
                sr_number: currentSrNumber,
              });
            }
          }
        }
        setStaffingRequests(finalStaffingList);

        const regionsData = regionsRes?.data;
        const regionsList = Array.isArray(regionsData)
          ? regionsData
          : (regionsData?.regionDetails
            || regionsData?.regions
            || regionsData?.data
            || regionsData?.result
            || []);
        setRegions(Array.isArray(regionsList) ? regionsList : []);

        const verticalsData = verticalsRes?.data;
        const verticalsList = Array.isArray(verticalsData)
          ? verticalsData
          : (verticalsData?.verticalDetails
            || verticalsData?.verticals
            || verticalsData?.data
            || verticalsData?.result
            || []);
        setVerticals(Array.isArray(verticalsList) ? verticalsList : []);

        const rolesData = rolesRes?.data;
        const rolesList = Array.isArray(rolesData)
          ? rolesData
          : (rolesData?.resourceTypeList || rolesData?.data || rolesData?.result || []);
        setRoles(Array.isArray(rolesList) ? rolesList : []);

        const roleExperienceMappingsData = roleExperienceMappingsRes?.data;
        const roleExperienceMappingsList = Array.isArray(roleExperienceMappingsData)
          ? roleExperienceMappingsData
          : (
            roleExperienceMappingsData?.roleExperienceMappings
            || roleExperienceMappingsData?.data?.roleExperienceMappings
            || roleExperienceMappingsData?.data
            || []
          );
        setRoleExperienceMappings(
          Array.isArray(roleExperienceMappingsList) ? roleExperienceMappingsList : [],
        );

        const experiencesData = experiencesRes?.data;
        const experiencesList = Array.isArray(experiencesData)
          ? experiencesData
          : (experiencesData?.employeeExperienceList || experiencesData?.data || []);
        setExperiences(Array.isArray(experiencesList) ? experiencesList : []);

        const locationsData = locationsRes?.data;
        const locationsList = Array.isArray(locationsData)
          ? locationsData
          : (locationsData?.locationTypeList || locationsData?.data || []);
        setLocationTypes(Array.isArray(locationsList) ? locationsList : []);

        const departmentsData = departmentsRes?.data;
        const departmentsList = Array.isArray(departmentsData)
          ? departmentsData
          : (departmentsData?.departments || departmentsData?.data || []);
        setDepartments(Array.isArray(departmentsList) ? departmentsList : []);
      } catch (metaErr) {
        console.error('Error loading Add TBH Resource metadata:', metaErr);
      } finally {
        setLoadingMetadata(false);
      }
    };

    fetchMetadata();
  }, [projectId, projectStatus]);

  // Load countries when region changes
  useEffect(() => {
    if (!formData.region_id) {
      setCountries([]);
      return;
    }

    const fetchCountries = async () => {
      try {
        const req = regionCountryData(formData.region_id);
        if (req) {
          const res = await UseAxios(req);
          const cData = res?.data;
          const cList = Array.isArray(cData)
            ? cData
            : (cData?.countryDetails || cData?.countries || cData?.data || cData?.result || []);
          setCountries(Array.isArray(cList) ? cList : []);
        }
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };

    fetchCountries();
  }, [formData.region_id]);

  const handleFieldChange = (field) => (ev) => {
    if (field === 'role_id') {
      tbhManualSelectionRef.current.roleId = true;
      tbhManualSelectionRef.current.experienceId = false;
      updateRoleAndResetExperience(setFormData, ev.target.value);
      return;
    }

    if (field === 'experience_id') {
      tbhManualSelectionRef.current.experienceId = true;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: ev.target.value,
    }));
  };

  const handleTbhChange = (ev) => {
    const selectedTbhId = ev.target.value;
    tbhManualSelectionRef.current = {
      roleId: false,
      experienceId: false,
    };
    setFormData((prev) => ({
      ...prev,
      tbh_id: selectedTbhId,
    }));
    setTbhAutoPopulated(false);
  };

  // Fetch TBH details when TBH ID is selected and auto-populate fields
  useEffect(() => {
    const fetchTbhDetails = async () => {
      if (!formData.tbh_id) {
        setTbhAutoPopulated(false);
        return;
      }

      // Skip if already auto-populated to prevent unnecessary API calls
      if (tbhAutoPopulated) {
        return;
      }

      const cleanTbhId = String(formData.tbh_id).replace(/^TBH-/, '');

      try {
        const response = await UseAxios(getTbhDetailsByTbhId(cleanTbhId));
        const tbhData = response?.data?.data || response?.data;

        if (tbhData) {
          let hasChanges = false;
          const nextValues = {};

          if (tbhData.regionId && tbhData.countryId) {
            nextValues.region_id = String(tbhData.regionId);
            nextValues.country_id = String(tbhData.countryId);
            hasChanges = true;
          }

          if (tbhData.employeeVerticalId) {
            nextValues.vertical_id = String(tbhData.employeeVerticalId);
            hasChanges = true;
          }

          if (tbhData.employeeDepartmentId) {
            nextValues.department_id = String(tbhData.employeeDepartmentId);
            hasChanges = true;
          }

          if (tbhData.employeeRoleId) {
            nextValues.role_id = String(tbhData.employeeRoleId);
            hasChanges = true;
          }

          if (tbhData.employeeExperienceId) {
            nextValues.experience_id = String(tbhData.employeeExperienceId);
            hasChanges = true;
          }

          if (tbhData.locationId) {
            nextValues.location_id = String(tbhData.locationId);
            hasChanges = true;
          }

          if (hasChanges) {
            setFormData((prev) => {
              const latestTbhId = String(prev.tbh_id || '').replace(/^TBH-/, '');

              if (latestTbhId !== cleanTbhId) {
                return prev;
              }

              return {
                ...prev,
                ...nextValues,
                ...(tbhManualSelectionRef.current.roleId ? { role_id: prev.role_id } : {}),
                ...(tbhManualSelectionRef.current.experienceId
                  ? { experience_id: prev.experience_id }
                  : {}),
              };
            });
            setTbhAutoPopulated(true);
          }
        }
      } catch (err) {
        console.error('Error fetching TBH details:', err);
        setTbhAutoPopulated(false);
      }
    };

    fetchTbhDetails();
  }, [formData.tbh_id, tbhAutoPopulated]);

  const handleBackdropClick = (ev) => {
    if (ev.target === ev.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setFormError(null);

    // Validations
    if (!formData.tbh_id) return setFormError('Please select a TBH Id');
    if (!formData.region_id) return setFormError('Please select a Region');
    if (!formData.country_id) return setFormError('Please select a Country');
    if (!formData.vertical_id) return setFormError('Please select a Vertical');
    if (!formData.role_id) return setFormError('Please select a Role');
    if (!formData.experience_id) return setFormError('Please select Employee Experience');
    if (!formData.location_id) return setFormError('Please select a Location');
    if (!formData.department_id) return setFormError('Please select a Department');
    if (
      !formData.allocation_pct
      || Number(formData.allocation_pct) < 1
      || Number(formData.allocation_pct) > 100
    ) {
      return setFormError('Allocation% must be between 1 and 100');
    }
    if (!formData.allocation_start_date) return setFormError('Please select a Start Date');
    if (!formData.allocation_end_date) return setFormError('Please select an End Date');
    if (dayjs(formData.allocation_end_date).isBefore(dayjs(formData.allocation_start_date))) {
      return setFormError('End Date cannot be before Start Date');
    }
    if (!formData.scrum_team_id) return setFormError('Please select a Scrum Team');

    setSubmittingForm(true);

    const payload = {
      project_id: Number(projectId),
      tbh_id: formData.tbh_id,
      request_id: Number(formData.staffing_req_id) || 0,
      region_id: Number(formData.region_id),
      country_id: Number(formData.country_id),
      vertical_id: Number(formData.vertical_id),
      role_id: Number(formData.role_id),
      experience_id: Number(formData.experience_id),
      location_id: Number(formData.location_id),
      department_id: formData.department_id ? Number(formData.department_id) : null,
      allocation_pct: Number(formData.allocation_pct),
      allocation_start_date: formData.allocation_start_date,
      allocation_end_date: formData.allocation_end_date,
      scrum_team_id: Number(formData.scrum_team_id),
      remark: formData.remark || '',
      is_active: 1,
    };

    const tbhId = allocationData?.id || allocationData?.tbhId;

    try {
      const request = tbhId
        ? updateTbhResourceAllocation(tbhId, payload)
        : createTbhResourceAllocation(payload);

      const response = await UseAxios(request);
      if (response && response.status >= 200 && response.status < 300) {
        if (typeof onRefreshData === 'function') {
          onRefreshData();
        }
        if (typeof snackbarOpen === 'function') {
          snackbarOpen(
            tbhId
              ? 'TBH Resource allocation updated successfully.'
              : 'TBH Resource allocated successfully.',
            'success',
          );
        }
        onClose();
      } else {
        const errorMsg = response?.data?.response?.data?.message
          || response?.data?.message
          || 'Failed to save TBH resource allocation';

        if (typeof snackbarOpen === 'function') {
          snackbarOpen(errorMsg, 'warning');
        }
      }
    } catch (submitErr) {
      const errorMsg = submitErr?.response?.data?.message
        || submitErr?.message
        || 'Failed to save TBH resource allocation';

      if (typeof snackbarOpen === 'function') {
        snackbarOpen(errorMsg, 'warning');
      }
    } finally {
      setSubmittingForm(false);
    }
  };

  return (
    <div className="add-tbh-modal-overlay" onClick={handleBackdropClick} role="presentation">
      <style>
        {'.MuiSnackbar-root { z-index: 100000 !important; }'}
      </style>
      <div className="add-tbh-modal-box">
        <div className="add-tbh-modal-header">
          <span className="add-tbh-modal-header-title">
            {allocationData ? 'Edit TBH Resource' : 'Add TBH Resource'}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="add-tbh-modal-close-btn"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="add-tbh-modal-body">
          {blockingMessage ? (
            <div className="add-tbh-modal-blocking-message">
              ⚠️ {blockingMessage}
            </div>
          ) : null}

          {!blockingMessage && loadingMetadata ? (
            <div className="add-tbh-modal-loading">
              Loading form options...
            </div>
          ) : null}

          {!blockingMessage && !loadingMetadata ? (
            <form id="tbh-resource-form" onSubmit={handleSubmit}>
              <div className="add-tbh-modal-form-fields">
                {formError && (
                  <div className="add-tbh-modal-form-error">
                    * {formError}
                  </div>
                )}

                <div className="add-tbh-modal-two-col">
                  <FieldSelect
                    labelText="TBH Id"
                    isRequired
                    value={formData.tbh_id}
                    onChange={handleTbhChange}
                    isDisabled={Boolean(allocationData)}
                  >
                    <option value="">Select</option>
                    {tbhEmployees
                      .filter(
                        (emp) => (emp.totalAllocationPct || 0) < 100
                          || String(emp.tbhId) === String(formData.tbh_id),
                      )
                      .map((emp) => (
                        <option key={emp.id} value={emp.tbhId}>
                          {emp.tbhId}
                        </option>
                      ))}
                  </FieldSelect>
                  <FieldSelect
                    labelText="Department"
                    isRequired
                    value={formData.department_id}
                    onChange={handleFieldChange('department_id')}
                    isDisabled={Boolean(formData.tbh_id)}
                  >
                    <option value="">Select</option>
                    {departments.map((dept) => (
                      <option key={dept.dept_id} value={String(dept.dept_id)}>
                        {dept.dept_name}
                      </option>
                    ))}
                  </FieldSelect>
                </div>

                <div className="add-tbh-modal-two-col">
                  <FieldSelect
                    labelText="Staffing Request"
                    value={formData.staffing_req_id}
                    onChange={handleFieldChange('staffing_req_id')}
                  >
                    <option value="">Select</option>
                    <option value="0">General Request</option>
                    {staffingRequests.map((sr) => {
                      const val = sr.request_id ?? sr.id ?? sr.value;
                      const label = sr.sr_number ?? sr.srNumber ?? sr.label ?? 'SR';
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                  <FieldSelect
                    labelText="Vertical"
                    isRequired
                    value={formData.vertical_id}
                    onChange={handleFieldChange('vertical_id')}
                    isDisabled={Boolean(formData.tbh_id)}
                  >
                    <option value="">Select</option>
                    {verticals.map((verticalItem) => {
                      const val = verticalItem.vertical_id
                        ?? verticalItem.verticalId
                        ?? verticalItem.id
                        ?? verticalItem.value;
                      const label = verticalItem.verticalName
                        || verticalItem.vertical_name
                        || verticalItem.vertical
                        || verticalItem.name
                        || verticalItem.label;
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                </div>

                <div className="add-tbh-modal-two-col">
                  <FieldSelect
                    labelText="Region"
                    isRequired
                    value={formData.region_id}
                    onChange={handleFieldChange('region_id')}
                    isDisabled={Boolean(formData.tbh_id)}
                  >
                    <option value="">Select</option>
                    {regions.map((regionItem) => {
                      const val = regionItem.region_id
                        ?? regionItem.regionId
                        ?? regionItem.id
                        ?? regionItem.value;
                      const label = regionItem.region_name
                        ?? regionItem.regionName
                        ?? regionItem.name
                        ?? regionItem.label;
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                  <FieldSelect
                    labelText="Country"
                    isRequired
                    value={formData.country_id}
                    onChange={handleFieldChange('country_id')}
                    isDisabled={Boolean(formData.tbh_id) || !formData.region_id}
                  >
                    <option value="">Select</option>
                    {countries.map((countryItem) => {
                      const val = countryItem.country_id
                        ?? countryItem.countryId
                        ?? countryItem.id
                        ?? countryItem.value;
                      const label = countryItem.country_name
                        || countryItem.countryName
                        || countryItem.name
                        || countryItem.label;
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                </div>

                <div className="add-tbh-modal-two-col">
                  <FieldSelect
                    labelText="Role"
                    isRequired
                    value={formData.role_id}
                    onChange={handleFieldChange('role_id')}
                    isDisabled={Boolean(formData.tbh_id)}
                  >
                    <option value="">Select</option>
                    {roles.map((roleItem) => {
                      const val = roleItem.type_id ?? roleItem.id ?? roleItem.value;
                      const label = roleItem.resource_type
                        || roleItem.resourceType
                        || roleItem.resource_type_name
                        || roleItem.resourceTypeName
                        || roleItem.label
                        || roleItem.name;
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                  <FieldSelect
                    labelText="Employee Experience"
                    isRequired
                    value={formData.experience_id}
                    onChange={handleFieldChange('experience_id')}
                    isDisabled={Boolean(formData.tbh_id)}
                  >
                    <option value="">Select</option>
                    {filteredExperiences.map((opt) => (
                      <option key={opt.id ?? opt.value} value={String(opt.id ?? opt.value ?? '')}>
                        {getExperienceLabel(opt)}
                      </option>
                    ))}
                  </FieldSelect>
                </div>

                <div className="add-tbh-modal-two-col">
                  <FieldSelect
                    labelText="Location"
                    isRequired
                    value={formData.location_id}
                    onChange={handleFieldChange('location_id')}
                    isDisabled={Boolean(formData.tbh_id)}
                  >
                    <option value="">Select</option>
                    {locationTypes.map((loc) => {
                      const val = loc.id ?? loc.value;
                      const label = loc.locationType || loc.location_type || loc.label || loc.name;
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                  <div>
                    <label className="add-tbh-modal-field-label">
                      Allocation% <span className="add-tbh-modal-required">*</span>
                    </label>
                    <input
                      type="number"
                      className="add-tbh-modal-field-input"
                      value={formData.allocation_pct}
                      onChange={handleFieldChange('allocation_pct')}
                      min="1"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <div className="add-tbh-modal-two-col">
                  <FieldSelect
                    labelText="Scrum Team"
                    isRequired
                    value={formData.scrum_team_id}
                    onChange={handleFieldChange('scrum_team_id')}
                  >
                    <option value="">Select</option>
                    {scrums.map((scrumItem) => {
                      const val = scrumItem.id ?? scrumItem.value;
                      const label = scrumItem.name
                        ?? scrumItem.scrum_names
                        ?? scrumItem.scrumName
                        ?? scrumItem.label;
                      return (
                        <option key={val} value={String(val)}>
                          {label}
                        </option>
                      );
                    })}
                  </FieldSelect>
                  <div aria-hidden="true" />
                </div>

                <div className="add-tbh-modal-two-col add-tbh-modal-datepicker-row">
                  <CustomDatePicker
                    label="Start Date"
                    placeholder="Choose Date"
                    value={
                      formData.allocation_start_date
                        ? dayjs(formData.allocation_start_date)
                        : null
                    }
                    onChange={(val) => setFormData(
                      (prev) => ({
                        ...prev,
                        allocation_start_date: val
                          ? val.format('YYYY-MM-DD')
                          : '',
                      }),
                    )}
                    displayFormat="DD/MM/YYYY"
                  />
                  <CustomDatePicker
                    label="End Date"
                    placeholder="Choose Date"
                    value={
                      formData.allocation_end_date
                        ? dayjs(formData.allocation_end_date)
                        : null
                    }
                    onChange={(val) => setFormData(
                      (prev) => ({
                        ...prev,
                        allocation_end_date: val
                          ? val.format('YYYY-MM-DD')
                          : '',
                      }),
                    )}
                    displayFormat="DD/MM/YYYY"
                  />
                </div>

                <div>
                  <label className="add-tbh-modal-field-label">Remarks</label>
                  <textarea
                    className="add-tbh-modal-textarea"
                    value={formData.remark}
                    onChange={handleFieldChange('remark')}
                    placeholder="Enter remarks (optional)"
                  />
                </div>

              </div>
            </form>
          ) : null}
        </div>

        <div className="add-tbh-modal-footer">
          <button
            type="button"
            className="add-tbh-modal-cancel-btn"
            onClick={onClose}
            disabled={submittingForm}
          >
            {blockingMessage ? 'Close' : 'Cancel'}
          </button>
          {!blockingMessage && (
            <button
              type="submit"
              form="tbh-resource-form"
              className={
                `add-tbh-modal-add-btn${submittingForm ? ' add-tbh-modal-add-btn--disabled' : ''}`
              }
              disabled={submittingForm}
            >
              {submittingForm ? 'Saving...' : (allocationData ? 'Save' : 'Add')}
            </button>
          )}
        </div>

      </div>
    </div>
  );
};

AddTbhResourceModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  projectStatus: PropTypes.string,
  onRefreshData: PropTypes.func,
  allocationData: PropTypes.object,
};

AddTbhResourceModal.defaultProps = {
  projectId: null,
  projectStatus: '',
  onRefreshData: () => {},
  allocationData: null,
};

export default AddTbhResourceModal;
